/**
 * Prize / leaderboard display helpers (admin UI).
 * Keep in sync with backend `libs/common/src/prize-leaderboard.ts` + `prize-tier.ts`
 * and Flutter `lib/utils/play_table_ranking.dart`.
 */
export const WIN_GREEN = "#00B786";
export const BRAND_RED = "#F12C2C";
export const TEXT_PRIMARY = "#2E3A59";
export const TEXT_SECONDARY = "#64748B";
export const LB_BORDER = "#EDF1F7";

export const PHONE_ORANGE = "#EA580C";
export const PRIZE_ORANGE = "#F97316";

export const maskPhone = (phone) => {
  if (!phone) return "";
  return String(phone).replace(/\d(?=\d{4})/g, "x");
};

/**
 * Quiz elapsed time is stored as H:MMM:SSS:MMM (from the mobile app timer).
 * Show the first three segments; full value stays in the title attribute.
 */
export const formatPlayTableTimeDisplay = (totalTime) => {
  if (totalTime == null || totalTime === "") return "—";
  const str = String(totalTime).trim();
  if (/exit/i.test(str)) return "Player exit in mid";
  const parts = str.split(":");
  if (parts.length === 4) {
    return `${parts[0]}:${parts[1]}:${parts[2]}`;
  }
  return str;
};

export const parseCorrectCount = (value) => {
  const parsed = parseInt(String(value ?? "0"), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const QUIZ_IN_PROGRESS_LABEL = "Playing...";

const rowExitOnMid = (row) =>
  row?.exitOnMid === true || String(row?.exitOnMid).toLowerCase() === "true";

/** Admin UI: quiz started (saveQuiz) but not finalised (updateQuiz) yet. */
export const isQuizInProgressAdmin = (row) => {
  if (!row || !rowExitOnMid(row)) return false;
  if (parseCorrectCount(row.correctQuestion) > 0) return false;

  const attempted = Number(row.attemptedQuestions) || 0;
  if (attempted > 0) return true;

  const startedAt = row.quizStartedAt || row.createdAt;
  if (!startedAt) return false;

  const createdMs = row.createdAt ? new Date(row.createdAt).getTime() : 0;
  const updatedMs = row.updatedAt ? new Date(row.updatedAt).getTime() : createdMs;
  if (updatedMs > createdMs + 2000) return false;

  return Date.now() - new Date(startedAt).getTime() < 70_000;
};

export const leaderboardDisplayRank = (row) => {
  if (!row) return "#";
  if (isQuizInProgressAdmin(row)) return "—";
  if (rowExitOnMid(row)) return "--";
  const rank = Number(row.rank) || 0;
  if (rank === 0 || parseCorrectCount(row.correctQuestion) <= 0) return "#";
  return rank > 0 ? `#${rank}` : "#";
};

export const formatLeaderboardTime = (row) => {
  if (isQuizInProgressAdmin(row)) return QUIZ_IN_PROGRESS_LABEL;
  if (rowExitOnMid(row)) return "Player exit in mid";
  return formatPlayTableTimeDisplay(row?.totalTime);
};

export const formatLeaderboardTimeFull = (row) => {
  if (isQuizInProgressAdmin(row)) return QUIZ_IN_PROGRESS_LABEL;
  if (rowExitOnMid(row)) return "Player exit in mid";
  const time = row?.totalTime;
  if (time == null || time === "") return "—";
  return String(time);
};

export const shouldShowLeaderboardQuestions = (row) => !isQuizInProgressAdmin(row);

export const entryDisplayName = (entry) => {
  const u = entry?.userDetails;
  if (!u) return entry?.userId || "User";
  const name = [u.name, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.phone || entry.userId || "User";
};

export const initialsFromName = (name) => {
  if (!name) return "??";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return String(name).slice(0, 2).toUpperCase();
};

export const parseWinRange = (winrange) => {
  const trimmed = String(winrange ?? "")
    .trim()
    .replace(/^#+/, "");
  if (!trimmed) return { start: 0, end: 0 };
  if (trimmed.includes("-")) {
    const parts = trimmed.split("-");
    if (parts.length === 2) {
      return {
        start: parseInt(parts[0].trim(), 10) || 0,
        end: parseInt(parts[1].trim(), 10) || 0,
      };
    }
  }
  const single = parseInt(trimmed, 10) || 0;
  return { start: single, end: single };
};

export const isRankInWinRange = (rank, winrange) => {
  const { start, end } = parseWinRange(winrange);
  if (start < 1 || end < start) return false;
  return rank >= start && rank <= end;
};

/** True when winrange is a usable prize tier (e.g. "1", "2-10") — not blank/#. */
export const isValidWinRange = (winrange) => {
  const { start, end } = parseWinRange(winrange);
  return start >= 1 && end >= start;
};

export const calculateDivision = (winrange, amountString) => {
  const amount = parseFloat(amountString) || 1;
  const rangeParts = String(winrange).split("-");
  if (rangeParts.length === 1) return amount;
  if (rangeParts.length === 2) {
    const minRange = parseInt(rangeParts[0], 10) || 0;
    const maxRange = parseInt(rangeParts[1], 10) || 0;
    const totalRange = maxRange - minRange + 1;
    return totalRange > 0 ? amount / totalRange : 0;
  }
  return 0;
};

export const normalizePrizeType = (prizeType) => {
  const value = prizeType?.trim()?.toUpperCase();
  if (value === "PRODUCT") return "PRODUCT";
  if (value === "CASH") return "CASH";
  return null;
};

export const isProductPrizeTier = (doc, product, allTiers = []) => {
  const normalized = normalizePrizeType(doc?.prizeType);
  if (normalized === "PRODUCT") return true;
  if (normalized === "CASH") return false;
  const rank = String(doc?.winrange ?? "").trim();
  if (rank !== "1" || product?.isFirstWinnerGotProduct !== "PRODUCT") return false;
  if (
    allTiers.some(
      (tier) =>
        String(tier.winrange ?? "").trim() !== "1" &&
        normalizePrizeType(tier.prizeType) === "PRODUCT"
    )
  ) {
    return false;
  }
  return true;
};

export const trimProductName = (name, maxLen = 18) => {
  const trimmed = String(name ?? "").trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen - 1)}…`;
};

export const formatComboRankLabel = (comboRanks) => {
  const ranks = String(comboRanks ?? "")
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
  const unique = [...new Set(ranks)].sort((a, b) => a - b);
  return unique.length > 0 ? unique.map((r) => `#${r}`).join(" + ") : "—";
};

export const comboWinningRowTitle = ({ comboRanks, label, prizeType }) => {
  const ranks = formatComboRankLabel(comboRanks);
  if (normalizePrizeType(prizeType) === "PRODUCT") return ranks;
  const trimmed = label?.trim() ?? "";
  if (!trimmed) return ranks;
  return `${ranks} · ${trimmed}`;
};

export const comboPrizeLabel = ({
  comboRanks,
  prizeType,
  productName,
  amount,
  productLabel,
}) => {
  if (normalizePrizeType(prizeType) === "PRODUCT") {
    const trimmed = productLabel?.trim() ?? "";
    if (trimmed) return trimProductName(trimmed, 18);
    return trimProductName(productName, 18);
  }
  const parsed = parseFloat(amount);
  if (Number.isNaN(parsed)) return `₹${amount ?? "0"}`;
  return `₹${parsed.toFixed(2)}`;
};

export const standardPrizeLabel = (doc, product, calculatedValue) => {
  if (isProductPrizeTier(doc, product, product?.productWinningDocuments)) {
    return trimProductName(product?.productName, 16);
  }
  return `₹${calculatedValue.toFixed(2)}`;
};

export const buildHistoryByRank = (history) => {
  const map = {};
  for (const row of history || []) {
    const rank = Number(row.rank) || 0;
    if (rank > 0 && !rowExitOnMid(row) && !map[rank]) {
      map[rank] = { ...row, rank };
    }
  }
  return map;
};

/** Merge multiple history slices; first source wins per rank. */
export const mergeHistoryByRank = (...histories) => {
  const map = {};
  for (const history of histories) {
    for (const [rank, row] of Object.entries(buildHistoryByRank(history))) {
      if (!map[rank]) map[rank] = row;
    }
  }
  return map;
};

export const winnersForWinRange = (winrange, historyByRank) => {
  const { start, end } = parseWinRange(winrange);
  if (start < 1 || end < start) return [];
  const winners = [];
  for (let rank = start; rank <= end; rank += 1) {
    const entry = historyByRank[rank];
    if (entry) winners.push({ entry, rank });
  }
  return winners;
};

export const K_WINNING_HISTORY_CHUNK_SIZE = 500;
export const K_WINNING_HISTORY_MAX_FETCH = 5000;
export const K_WINNING_HISTORY_MIN_FETCH = 50;

/** Prize tiers in ascending rank order (1, 2-5, 6-10, …) — matches Flutter `sortedWinningDocuments`. */
export const sortedWinningDocuments = (docs) => {
  const list = (docs || []).filter((d) => isValidWinRange(d.winrange));
  list.sort((a, b) => {
    const rangeA = parseWinRange(a.winrange);
    const rangeB = parseWinRange(b.winrange);
    const byStart = rangeA.start - rangeB.start;
    if (byStart !== 0) return byStart;
    return rangeA.end - rangeB.end;
  });
  return list;
};

/** Largest rank end among prize tiers (optional floor for defaults). */
export const maxRankFromWinningDocs = (winningDocs, floor = 50) => {
  let max = floor;
  for (const doc of winningDocs || []) {
    const { end } = parseWinRange(doc.winrange);
    if (end > max) max = end;
  }
  return max;
};

/** How many ranked play-table rows we need to resolve all configured prize tiers. */
export const winningHistoryFetchSize = (docs, rankCeiling = 0) => {
  const maxRank = maxRankFromWinningDocs(docs, 0);
  if (maxRank <= 0) return 0;
  let target = maxRank;
  // Live contests can configure prize ranks far beyond current entries
  // (e.g. car at 42_000_000). Cap fetch to the current ranked field.
  const ceiling = Number(rankCeiling) || 0;
  if (ceiling > 0) target = Math.min(target, ceiling);
  if (target < K_WINNING_HISTORY_MIN_FETCH) return target;
  return target;
};

export const payoutLabelForRow = (row, product, winningDocs) => {
  if (row?.payoutAmount) return `₹${row.payoutAmount}`;
  if (row?.payoutStatus === "PRODUCT") return "Product prize";
  if (!product || !winningDocs?.length || row.rank < 1) return null;

  for (const doc of winningDocs) {
    if (!isRankInWinRange(row.rank, doc.winrange)) continue;
    if (isProductPrizeTier(doc, product, winningDocs)) {
      return trimProductName(product.productName, 18);
    }
    const amount = calculateDivision(doc.winrange, doc.amount);
    if (amount > 0) return `₹${amount.toFixed(2)}`;
  }
  return null;
};
